import { Avatar, Box, Divider, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MoreHorizOutlinedIcon from '@mui/icons-material/MoreHorizOutlined';
import CancelSessionIcon from '../../../assets/SessionIcon/cancel-session-icon.svg';
import ReportIcon from '../../../assets/SessionIcon/report-issue-icon.svg';
import './PrimeSessionDetails.css';
import OneOnOneSessionIcon from '../../../assets/SessionIcon/one-on-one-session-icon.svg';
import GroupSessionIcon from '../../../assets/SessionIcon/group-session-icon.svg';
import PrimeSessionIcon from '../../../assets/SessionIcon/prime-session-icon.svg';
import RescheduleIcon from '../../../assets/SessionIcon/reschedule-icon.svg';
import dayjs from 'dayjs';
import { useTranslation } from '../../../i18n/LanguageContext';
import CancelSessionModal from '../CancelSessionModal/CancelSessionModal';
import RescheduleSessionModal from '../RescheduleSessionModal/RescheduleSessionModal';
import ReportModal from '../ReportModal/ReportModal';
import MaleIcon from '@mui/icons-material/Male';
import FemaleIcon from '@mui/icons-material/Female';
import ZoomIcon from '../../../assets/SessionIcon/zoom-icon.svg';
import StarIcon from '@mui/icons-material/Star';
import { Dialog, DialogActions, DialogContent, DialogTitle, Button } from '@mui/material';
import { axiosInstance } from '../../../utils/tokenUtils';
import { toast } from 'react-hot-toast';

const SessionTypeColor = {
  one_on_one: {
    color: '#0A5EF3',
    bgcolor: '#E8F0FF',
    value: 'One-On-One Session',
    icon: OneOnOneSessionIcon,
  },
  group: {
    color: '#7BCF8C',
    bgcolor: '#FAFFFB',
    value: 'Group Session',
    icon: GroupSessionIcon,
  },
  prime: {
    color: '#AD8B00',
    bgcolor: '#FFFBF0',
    value: 'Prime Session',
    icon: PrimeSessionIcon,
  },
};

const LevelToTeachColor = {
  elementary: {
    color: '#0A5EF3',
    bgcolor: '#E8F0FF',
    value: 'Elementary',
  },
  university: {
    color: '#14B334',
    bgcolor: '#F2FFF5',
    value: 'University',
  },
  high_school: { color: '#E0AD14', bgcolor: '#FFFBF0', value: 'High School' },
  middle_school: {
    color: '#6B60E7',
    bgcolor: '#F0EFFF',
    value: 'Middle School',
  },
};

const PrimeSessionDetails = ({ singleSessionDetails }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const { translate } = useTranslation();
  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [openRescheduleModal, setOpenRescheduleModal] = useState(false);
  const [openReportModal, setOpenReportModal] = useState(false);
  const [alreadyDisputedOpen, setAlreadyDisputedOpen] = useState(false);

  const handleMenuClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const checkDisputeThenOpen = async () => {
    try {
      const res = await axiosInstance.get(`/teachers/sessions/dispute/${singleSessionDetails?.id}`);
      if (res?.data && !res.data?.detail) {
        setAlreadyDisputedOpen(true);
        return;
      }
      if (res?.data?.detail === 'Dispute not found for this session') {
        setOpenReportModal(true);
        return;
      }
      setAlreadyDisputedOpen(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail === 'Dispute not found for this session' || err?.response?.status === 404) {
        setOpenReportModal(true);
      } else {
        setAlreadyDisputedOpen(true);
      }
    }
  };

  // Convert a UTC timestamp string to local Dayjs
  const toLocalFromUTC = utcString => {
    const utc = dayjs(utcString);
    return dayjs(new Date(utc.valueOf() - utc.toDate().getTimezoneOffset() * 60000));
  };

  const formatTimeInterval = (startTime, lengthInMinutes) => {
    if (!startTime || !lengthInMinutes) return '---';

    // Convert UTC start time to local
    const startTimeLocal = toLocalFromUTC(startTime);

    // Calculate end time based on start time and session length
    const endTimeLocal = dayjs(startTimeLocal).add(lengthInMinutes, 'minutes');

    // Format start and end times
    const startFormat = startTimeLocal.format('h:mm a');
    const endFormat = endTimeLocal.format('h:mm a');

    // Return formatted time range
    return `${startFormat} - ${endFormat}`;
  };

  const handleJoin = () => {
    const link = singleSessionDetails?.meeting_link;
    if (!link) {
      toast.error('Please check back at the session time');
      return;
    }
    window.open(link, '_blank', 'noopener,noreferrer');
  };

  // Generate session dates for two weeks based on start date
  const getSessionDates = () => {
    if (!singleSessionDetails?.start_time) return { week1: [], week2: [] };

    // Convert UTC start time to local
    const startTimeUTC = dayjs(singleSessionDetails.start_time);
    const startTimeLocal = dayjs(
      new Date(startTimeUTC.valueOf() - startTimeUTC.toDate().getTimezoneOffset() * 60000)
    );

    const startOfWeek = startTimeLocal.startOf('week');

    // Get first week dates (Mon-Wed)
    const week1 = [];
    for (let i = 1; i <= 3; i++) {
      // Monday to Wednesday
      const date = startOfWeek.add(i, 'day');
      week1.push({
        day: date.format('ddd'),
        date: date.format('D MMM YYYY'),
      });
    }

    // Get second week dates (Mon-Wed)
    const week2 = [];
    for (let i = 8; i <= 10; i++) {
      // Monday to Wednesday of next week
      const date = startOfWeek.add(i, 'day');
      week2.push({
        day: date.format('ddd'),
        date: date.format('D MMM YYYY'),
      });
    }

    return { week1, week2 };
  };

  const sessionDates = getSessionDates();

  return (
    <>
      <Box className="flex flex-col gap-4">
        <Box className="flex justify-between items-center">
          <Box className="flex items-center gap-2">
            <Typography
              className="navigation-prime-text"
              onClick={() => navigate('/instructor/sessions')}
            >
              {translate('sessionDetails', 'sessionText')} {'>'}
              {'>'}{' '}
              {singleSessionDetails?.status === 'upcoming'
                ? `${translate('sessionDetails', 'upcomingText')}`
                : singleSessionDetails?.status === 'completed'
                  ? `${translate('sessionDetails', 'completedText')}`
                  : `${translate('sessionDetails', 'cancelledText')}`}{' '}
              {'>'}
              {'>'} {translate('sessionDetails', 'studentProfileText')} {'>'}
              {'>'}
            </Typography>
            <Typography className="profile-text">
              {translate('sessionDetails', 'detailsText')}
            </Typography>
          </Box>
          <Box className="flex items-center gap-4">
            {singleSessionDetails?.status !== 'cancelled' && (
              <IconButton data-testid="more-options-button" onClick={e => handleMenuClick(e)}>
                {anchorEl ? (
                  <MoreHorizOutlinedIcon sx={{ color: '#0A5EF3' }} />
                ) : (
                  <MoreHorizOutlinedIcon />
                )}
              </IconButton>
            )}
          </Box>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            PaperProps={{
              sx: { width: '200px' },
            }}
          >
            {/* Prime sessions: Reschedule when upcoming and NOT already rescheduled; no cancel option */}
            {singleSessionDetails?.session_type === 'prime' &&
              singleSessionDetails?.status === 'upcoming' &&
              singleSessionDetails?.status !== 'rescheduled' &&
              singleSessionDetails?.status !== 'resheduled' && (
                <MenuItem onClick={() => setOpenRescheduleModal(true)}>
                  <Box className="flex gap-2 items-center">
                    <img src={RescheduleIcon} alt="Reschedule" />
                    <div>
                      {translate('sessionDetails', 'rescheduleText') || 'Reschedule Session'}
                    </div>
                  </Box>
                </MenuItem>
              )}

            {/* Show Report for completed sessions */}
            {singleSessionDetails?.status === 'completed' && (
              <MenuItem onClick={() => checkDisputeThenOpen()}>
                <Box className="flex gap-2 items-center">
                  <img src={ReportIcon} alt="Report" />
                  <div>{translate('sessionDetails', 'reportIssuesText')}</div>
                </Box>
              </MenuItem>
            )}
          </Menu>
        </Box>

        {/* Session Detail */}
        <Box className="prime-session-single-card-container">
          <Typography className="prime-session-heading-text">Session Detail</Typography>
          <Box className="flex flex-wrap items-center content-center gap-2 self-stretch p-3 rounded-md bg-[#FFF] w-[100%]">
            <Box className="flex flex-col gap-2 w-[23%]">
              <Typography className="prime-session-details-key-text">
                {translate('sessionDetails', 'subjectText') || 'Subject'}
              </Typography>
              <Typography className="prime-session-details-value-text">
                {singleSessionDetails?.subject || 'Mathematics'}
              </Typography>
            </Box>
            <Box className="flex flex-col gap-2 w-[23%]">
              <Typography className="prime-session-details-key-text">
                {translate('sessionDetails', 'dateText') || 'Date'}
              </Typography>
              <Typography className="prime-session-details-value-text">
                {singleSessionDetails?.start_time
                  ? (() => {
                      const startLocal = toLocalFromUTC(singleSessionDetails.start_time);
                      const endLocal = startLocal.add(7, 'day');
                      return `${startLocal.format('D MMM YYYY')} - ${endLocal.format('D MMM YYYY')}`;
                    })()
                  : '---'}
              </Typography>
            </Box>
            <Box className="flex flex-col gap-2 w-[23%]">
              <Typography className="prime-session-details-key-text">
                {translate('sessionDetails', 'timingText') || 'Timing'}
              </Typography>
              <Typography className="prime-session-details-value-text">
                {singleSessionDetails?.start_time
                  ? formatTimeInterval(
                      singleSessionDetails.start_time,
                      singleSessionDetails.length_in_minutes
                    )
                  : '---'}
              </Typography>
            </Box>
            <Box className="flex flex-col gap-2 w-[23%]">
              <Typography className="prime-session-details-key-text">
                {translate('sessionDetails', 'sessionTypeText') || 'Session Type'}
              </Typography>
              <Typography
                className="prime-session-details-value-text"
                sx={{
                  color: SessionTypeColor[singleSessionDetails?.session_type]?.color,
                }}
              >
                {singleSessionDetails?.session_type
                  ? SessionTypeColor[singleSessionDetails?.session_type]?.value
                  : '---'}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Session Dates */}
        <Box className="prime-session-single-card-container">
          <Typography className="prime-session-heading-text">Session Dates</Typography>
          <Box className="flex gap-4 w-full">
            {/* Week 01 */}
            <Box className="prime-session-single-week-card">
              <Typography className="prime-session-week-key-text">WEEK 01</Typography>
              {sessionDates.week1.map((date, index) => (
                <Box key={index} className="flex flex-col">
                  <Typography className="prime-session-week-value-text">
                    {date.day} {date.date.split(' ')[0]} {date.date.split(' ')[1]},{' '}
                    {date.date.split(' ')[2]}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Week 02 */}
            <Box className="prime-session-single-week-card">
              <Typography className="prime-session-week-key-text">WEEK 02</Typography>
              {sessionDates.week2.map((date, index) => (
                <Box key={index} className="flex flex-col">
                  <Typography className="prime-session-week-value-text">
                    {date.day} {date.date.split(' ')[0]} {date.date.split(' ')[1]},{' '}
                    {date.date.split(' ')[2]}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {/* Student Cards */}
        {singleSessionDetails?.students &&
          singleSessionDetails.students.map(student => (
            <Box className="prime-session-single-card-container" key={student?.id}>
              <Box className="flex items-center justify-between w-full">
                <Box className="flex items-center gap-3">
                  <Avatar
                    src={student?.user?.profile_image}
                    alt={student?.user?.name}
                    data-testid="student-avatar"
                    sx={{ width: '45px', height: '45px' }}
                  />
                  <Box className="flex flex-col">
                    <Box className="flex items-center gap-4">
                      <Typography className="teacher-prime-session-text">
                        {student?.user?.name}
                      </Typography>
                      <Divider
                        orientation="vertical"
                        flexItem
                        sx={{
                          height: '16px',
                          mt: 1,
                          borderColor: '#D5DEE3',
                        }}
                      />
                      <Box
                        style={{
                          height: '30px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                          gap: '10px',
                          color: SessionTypeColor[singleSessionDetails?.session_type]?.color,
                          fontFamily: 'Poppins !important',
                          fontSize: '14px !important',
                          fontStyle: 'normal !important',
                          fontWeight: '400 !important',
                          lineHeight: 'normal !important',
                        }}
                      >
                        <img
                          src={SessionTypeColor[singleSessionDetails?.session_type]?.icon}
                          alt={SessionTypeColor[singleSessionDetails?.session_type]?.value}
                        />
                        <Typography>
                          {singleSessionDetails?.session_type
                            ? SessionTypeColor[singleSessionDetails?.session_type]?.value
                            : '---'}
                        </Typography>
                      </Box>
                    </Box>
                    <Box
                      style={{
                        height: '30px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        width: '120px',
                        padding: '3px 6px',
                        borderRadius: '12px !important',
                        backgroundColor: '#FFFBF0',
                        color: LevelToTeachColor[student?.school_level]?.color,
                        fontFamily: 'Poppins !important',
                        fontSize: '10px !important',
                        fontStyle: 'normal !important',
                        fontWeight: '400 !important',
                        lineHeight: 'normal !important',
                      }}
                    >
                      <Typography>
                        {student?.school_level
                          ? LevelToTeachColor[student?.school_level]?.value
                          : '---'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>

              <Box className="flex flex-wrap items:center content-center gap-4 self-stretch p-3 rounded-md bg-[#FAFAFA] w-[100%] mt-3">
                <Typography className="prime-session-heading-text">
                  {translate('sessionDetails', 'studentDetailsText') || 'Student Detail'}
                </Typography>
                <Box className="flex flex-wrap items-center content-center gap-2 self-stretch p-3 rounded-md bg-[#FFF] w-[100%]">
                  <Box className="flex flex-col gap-2 w-[23%]">
                    <Typography className="prime-session-details-key-text">School Level</Typography>
                    <Typography className="prime-session-details-value-text">
                      {student?.school_level
                        ? LevelToTeachColor[student?.school_level]?.value
                        : 'High School'}
                    </Typography>
                  </Box>
                  <Box className="flex flex-col gap-2 w-[23%]">
                    <Typography className="prime-session-details-key-text">Country</Typography>
                    <Typography className="prime-session-details-value-text">
                      {student?.country || '---'}
                    </Typography>
                  </Box>
                  <Box className="flex flex-col gap-2 w-[23%]">
                    <Typography className="prime-session-details-key-text">Gender</Typography>
                    <Box className="flex items-center gap-1">
                      {student?.gender === 'Female' ? (
                        <>
                          <FemaleIcon sx={{ color: '#E83E8C', fontSize: 20 }} />
                          <Typography className="prime-session-details-value-text">
                            Female
                          </Typography>
                        </>
                      ) : (
                        <>
                          <MaleIcon sx={{ color: '#007BFF', fontSize: 20 }} />
                          <Typography className="prime-session-details-value-text">Male</Typography>
                        </>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          ))}
      </Box>

      {/* Reviews Section - keep as is if needed; mobile only shows reviews for completed one-on-one */}
      {singleSessionDetails?.status === 'completed' &&
        singleSessionDetails?.reviews &&
        singleSessionDetails.reviews.length > 0 && (
          <Box className="prime-session-single-card-container">
            <Typography className="prime-session-heading-text">
              {translate('sessionDetails', 'reviewsText') || 'Reviews'}
            </Typography>
            <Box className="flex flex-col gap-4 w-[100%]">
              {singleSessionDetails.reviews.map((review, index) => (
                <Box
                  key={review.id || index}
                  className="flex flex-col gap-3 p-3 rounded-md bg-[#FAFAFA] w-[100%]"
                >
                  <Box className="flex items-center gap-3">
                    <Avatar
                      src={review.student?.profile_image || ''}
                      alt={review.student?.name || 'Student'}
                      sx={{ width: '40px', height: '40px' }}
                    />
                    <Box className="flex flex-col">
                      <Typography
                        className="prime-session-details-value-text"
                        sx={{ fontWeight: '600' }}
                      >
                        {review.student?.name || '---'}
                      </Typography>
                      <Box className="flex items-center gap-1">
                        {[...Array(5)].map((_, starIndex) => (
                          <StarIcon
                            key={starIndex}
                            sx={{
                              color: starIndex < review.rating ? '#FFD700' : '#E0E0E0',
                              fontSize: '16px',
                            }}
                          />
                        ))}
                        <Typography
                          className="prime-session-details-value-text"
                          sx={{ fontSize: '12px', color: '#666' }}
                        >
                          ({review.rating}/5)
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  {review.feedback && (
                    <Typography
                      className="prime-session-details-value-text"
                      sx={{ fontSize: '14px', lineHeight: '1.5' }}
                    >
                      "{review.feedback}"
                    </Typography>
                  )}
                  <Typography
                    className="prime-session-details-key-text"
                    sx={{ fontSize: '12px', color: '#666' }}
                  >
                    {review.created_at
                      ? dayjs(review.created_at).format('DD MMM YYYY, h:mm A')
                      : '---'}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

      <Box className="join-button-prime-container">
        {singleSessionDetails?.status !== 'cancelled' &&
          singleSessionDetails?.status !== 'completed' && (
            <Box className="join-button-prime" onClick={handleJoin}>
              <img src={ZoomIcon} alt="Zoom" />
              <Typography className="join-button-text">Start Session</Typography>
            </Box>
          )}
      </Box>

      <CancelSessionModal
        open={openCancelModal}
        onClose={() => setOpenCancelModal(false)}
        sessionId={singleSessionDetails?.id}
        sessionType="prime"
      />

      <RescheduleSessionModal
        open={openRescheduleModal}
        onClose={() => setOpenRescheduleModal(false)}
        sessionId={singleSessionDetails?.id}
      />
      <ReportModal
        open={openReportModal}
        onClose={() => setOpenReportModal(false)}
        sessionDetails={singleSessionDetails}
        onSuccess={() => {
          console.log('Report submitted successfully');
        }}
      />

      <Dialog open={alreadyDisputedOpen} onClose={() => setAlreadyDisputedOpen(false)}>
        <DialogTitle>You have already raised the dispute</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            A dispute has already been created for this session.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAlreadyDisputedOpen(false)}>OK</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PrimeSessionDetails;
